/**
 * Ryan O'Dowd
 * 2018-02-05
 * © Copyright 2018 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
// @TODO: import * as RNLocalize from 'react-native-localize'; // @TODO: this has to be treeshaked
import {
  Platform,
  View,
} from 'react-native';
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  en,
  registerTranslation,
} from 'react-native-paper-dates';
import {
  fetchUser,
  logout,
  saveFcmToken,
} from '@utils/store/actions';
import Auth from '@containers/Auth';
import ForceUpgrade from './ForceUpgrade';
import Home from '@containers/Home';
import Outlet from './RouterOutlet';
import ScreenWrapper from '@containers/ScreenWrapper';
import WebPageStub from '@website/containers/WebPageStub';
import fb from '@utils/firebase';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities  from '@utils/utilities';

registerTranslation('en', en); // @TODO: i18n support for other locales

const App = () => {
  const dispatch = hooks.useTypedDispatch();
  const [_localeId, setLocaleId] = useState(1); // @TODO: useState(RNLocalize.getLocales().map((lo) => lo.languageCode).filter((lc) => ['en', 'es'].includes(lc)).shift() || 'en'); // @NOTE: this is only used for initial open // @TODO: switch to useing id instead of code
  const styles = hooks.useStyles(getStyles);
  const navigation = hooks.useNavigation();
  const locationState = hooks.useLocationState();

  const userSettings = hooks.useTypedSelector((state) => state.userSettings);

  const localeIdRef = useRef(_localeId);

  const minSupportedVersion = hooks.useTypedSelector((state) => state.versionSupport.minimum_supported_version);
  const mostRecentVersion: string = hooks.useTypedSelector((state) => state.versionSupport.most_recent_version);
  const currentAppVersionNotSupported = minSupportedVersion !== globals.appVersionNumber && utilities.isSecondOlderOrSameAsFirst(minSupportedVersion, globals.appVersionNumber);

  const oakUser = hooks.useTypedSelector((state) => state.oakUser);

  const [_authInitialized, setAuthInitialized] = useState(false);
  const [_userAuthenticated, setUserAuthenticated] = useState(false);

  hooks.useFetchInitialData(userSettings?.locale_id || _localeId, _authInitialized);

  useEffect(() => {
    if (oakUser?.id && Platform.OS !== 'web') {
      // register for push notifications
      const _saveFcmToken = async () => {
        try {
          const authStatus = await fb.requestPermission(fb.messaging);
          if (authStatus === 1) {
            const fcmToken = await fb.getToken(fb.messaging);
            if (fcmToken) {
              const [deviceId] = fcmToken.split(':'); // @TODO: better way to get this?
              dispatch(saveFcmToken(fcmToken, deviceId, Platform.OS));
            }
          }
        } catch (e) {
          fb.log(fb.crashlytics, `fcm issue: \`${e}\``);
        }
      };

      _saveFcmToken();
    }
  }, [dispatch, oakUser?.id]);

  useEffect(() => {
    localeIdRef.current = _localeId;
  }, [_localeId]);

  useEffect(() => {
    const authUnsubscribe = fb.onAuthStateChanged(fb.auth, (authUser) => {
      if (authUser) {
        fb.setUserId(fb.crashlytics, authUser.uid);
        setUserAuthenticated(true);
      } else {
        setUserAuthenticated(false);
      }

      setAuthInitialized(true);
    });

    return () => {
      authUnsubscribe();

      if (Platform.OS === 'web') { // @TODO: a bit of a leaky abstraction, but not sure where else to put this
        window.localStorage.removeItem('hasAlreadyDoneAutoQuickGoal');
      }
    };
  }, []);

  useEffect(() => {
    if (_authInitialized && !_userAuthenticated && !!oakUser) {
      // user is logged in according to redux but not in firebase; logout after a delay to make sure that it's not just a race condition with firebase trying to restore a valid session
      const timeout = setTimeout(() => {
        setUserAuthenticated(false);
        dispatch(logout());
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [dispatch, _authInitialized, _userAuthenticated, oakUser]);

  useEffect(() => {
    if (_authInitialized && _userAuthenticated) {
      dispatch(fetchUser(userSettings?.locale_id || localeIdRef.current));
    }
  }, [_authInitialized, _userAuthenticated, dispatch, userSettings?.locale_id]);

  useEffect(() => {
    if (_userAuthenticated && Platform.OS === 'web' && locationState.redirectUrl) {
      const timer = setTimeout(() => {
        // @TODO: seems like there's a race condition for setting the loggged-in user?
        navigation.navigate(locationState.redirectUrl);
        // @TODO: remove redirecturl from url
      }, 100);

      return () => clearTimeout(timer);
    } // @TODO: redirect to /app if no redirectUrl?
  }, [navigation, locationState.redirectUrl, _userAuthenticated]);

  if (currentAppVersionNotSupported && mostRecentVersion) {
    return <ForceUpgrade mostRecentVersion={mostRecentVersion} />;
  }

  if (!_authInitialized) {
    return (
      <ScreenWrapper>
        {null}
      </ScreenWrapper>
    );
  }

  const authTypes: any = ['google', 'apple', 'email'];
  if (Platform.OS !== 'web') {
    authTypes.push('anonymous');
  }

  if (!oakUser) { // @TODO: probably shouldn't rely on redux for logged-in status...not good for web because redux may not be wiped, but there's no firebase user // @TODO: should this be fb.auth.currentUser? not reactive, but more accurate (maybe a local state variable just for "logged in" or soemthing insstead of redux)
    return (
      <Auth
        authTypes={authTypes}
        i18nSupport={false} // @TODO: add spanish support back later
        localeId={_localeId}
        setLocaleId={(lid) => setLocaleId(lid)}// @TODO: on web, this could be moved into main header? when there's better support maybe
      />
      // @TODO: on login, show loading indicator because it takes about a second or two to do to next screen due to back-end calls
    );
  }

  if (Platform.OS === 'web') {
    return (
      <WebPageStub
        isWebApp={true}
        hideTabs={true}
        fullWidth={true}// @NOTE: this is okay because every page in web app should have ScreenWrapper in it
      >{/* @TODO: add prop for isApp or something so that background is always correct and to hide (or change) the nav options */}
        <Outlet />
      </WebPageStub>
    );
  } else {
    return (
      <View style={styles.container}>
        <Home />
      </View>
    );
  }
};

export default App;
